/**
 * A página aberta pelo QR de uma versão emitida (E145.F2, GUS-181, ADR-0099 §2.6).
 *
 * Sem login: a chave vem do fragmento do endereço (`/acesso#…`), que o navegador não manda ao
 * servidor; a página a envia no corpo do pedido. Mostra a versão, o código registrado e só as
 * saídas que a versão libera; a chave inválida, cancelada ou substituída recebe a mesma recusa,
 * sem dizer o que a versão guarda.
 */
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Blueprint, Button, MonoData } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import {
  AcessoPorQrError,
  abrirVersao,
  baixarSaida,
  chaveDoEndereco,
  type MotivoDaRecusaDoAcesso,
  type TipoDeSaida,
  type VersaoLiberadaData,
} from '@/lib/acesso_por_qr';
import { createApiClient } from '@/lib/api';
import { salvarArquivoBaixado } from '@/lib/http_download';
import { formatarDataHora } from '@/lib/trilha_format';

const t = ptBR.acessoControlado.publico;

type Leitura =
  | { fase: 'carregando' }
  | { fase: 'pronta'; versao: VersaoLiberadaData }
  | { fase: 'recusada'; motivo: MotivoDaRecusaDoAcesso };

type Download = { fase: 'parado' } | { fase: 'baixando' } | { fase: 'baixado'; codigo: string };

function motivoDe(err: unknown): MotivoDaRecusaDoAcesso {
  return err instanceof AcessoPorQrError ? err.motivo : 'rede';
}

export function AcessoPorQrPage() {
  const { hash } = useLocation();
  const chave = chaveDoEndereco(hash);
  const client = useMemo(() => createApiClient(), []);
  const [leitura, setLeitura] = useState<Leitura>({ fase: 'carregando' });
  const [tentativa, setTentativa] = useState(0);

  useEffect(() => {
    let vivo = true;
    if (!chave) {
      setLeitura({ fase: 'recusada', motivo: 'indisponivel' });
      return undefined;
    }
    setLeitura({ fase: 'carregando' });
    abrirVersao(client, chave)
      .then((versao) => {
        if (vivo) setLeitura({ fase: 'pronta', versao });
      })
      .catch((err: unknown) => {
        if (vivo) setLeitura({ fase: 'recusada', motivo: motivoDe(err) });
      });
    return () => {
      vivo = false;
    };
  }, [client, chave, tentativa]);

  return (
    <Blueprint
      as="section"
      data-testid="acesso-por-qr-page"
      aria-labelledby="acesso-por-qr-title"
      className="bg-bg px-8 py-[34px]"
    >
      <p className="mb-2.5 font-titulo text-rotulo uppercase text-fg-dim">{t.eyebrow}</p>
      {leitura.fase === 'carregando' ? (
        <p role="status" className="text-body-sm text-fg-muted">
          {t.carregando}
        </p>
      ) : null}
      {leitura.fase === 'recusada' ? (
        <Recusa motivo={leitura.motivo} onTentarDeNovo={() => setTentativa((n) => n + 1)} />
      ) : null}
      {leitura.fase === 'pronta' ? (
        <Versao versao={leitura.versao} baixar={(tipo) => baixarSaida(client, chave, tipo)} />
      ) : null}
    </Blueprint>
  );
}

function textoDaRecusa(motivo: MotivoDaRecusaDoAcesso): string {
  if (motivo === 'rede') return t.erroRede;
  if (motivo === 'muitas_tentativas') return t.muitasTentativas;
  return t.recusa;
}

function Recusa({
  motivo,
  onTentarDeNovo,
}: {
  motivo: MotivoDaRecusaDoAcesso;
  onTentarDeNovo: () => void;
}) {
  return (
    <>
      <h1 id="acesso-por-qr-title" className="font-display text-[32px] leading-none text-fg">
        {t.recusaTitulo}
      </h1>
      <p role="alert" data-testid={`acesso-recusa-${motivo}`} className="mt-3 text-body-sm text-fg">
        {textoDaRecusa(motivo)}
      </p>
      {motivo === 'rede' ? (
        <Button type="button" className="mt-6" onClick={onTentarDeNovo}>
          {t.tentarDeNovo}
        </Button>
      ) : null}
    </>
  );
}

interface VersaoProps {
  versao: VersaoLiberadaData;
  baixar: (tipo: TipoDeSaida) => ReturnType<typeof baixarSaida>;
}

function Versao({ versao, baixar }: VersaoProps) {
  const [download, setDownload] = useState<Download>({ fase: 'parado' });
  const [falhou, setFalhou] = useState<MotivoDaRecusaDoAcesso | null>(null);

  async function aoBaixar(tipo: TipoDeSaida) {
    setDownload({ fase: 'baixando' });
    setFalhou(null);
    try {
      const arquivo = await baixar(tipo);
      salvarArquivoBaixado(arquivo);
      setDownload({ fase: 'baixado', codigo: arquivo.codigoVerificacao ?? versao.codigo });
    } catch (err) {
      setDownload({ fase: 'parado' });
      setFalhou(motivoDe(err));
    }
  }

  return (
    <>
      <h1 id="acesso-por-qr-title" className="font-display text-[32px] leading-none text-fg">
        {t.titulo}
      </h1>
      <p className="mt-1.5 text-body-sm text-fg-muted">{t.intro}</p>
      <p className="mt-6 text-body-sm text-fg" data-testid="acesso-versao">
        {t.versao
          .replace('{versao}', String(versao.versao))
          .replace('{data}', formatarDataHora(versao.emitido_em))}
      </p>
      <p className="mt-3 text-caption text-fg-dim">{t.codigo}</p>
      <MonoData className="break-all">{versao.codigo}</MonoData>
      <p className="mt-1 text-caption text-fg-dim">{t.codigoExplica}</p>
      {versao.saidas.length === 0 ? (
        <p className="mt-6 text-body-sm text-fg-muted">{t.semSaida}</p>
      ) : (
        <div className="mt-6 flex flex-wrap gap-3">
          {versao.saidas.map(({ tipo }) => (
            <Button
              key={tipo}
              type="button"
              disabled={download.fase === 'baixando'}
              onClick={() => void aoBaixar(tipo)}
              data-testid={`acesso-baixar-${tipo}`}
            >
              {download.fase === 'baixando' ? t.baixando : t.baixar[tipo]}
            </Button>
          ))}
        </div>
      )}
      {download.fase === 'baixado' ? (
        <p role="status" className="mt-3 text-caption text-fg-muted">
          {t.baixado.replace('{codigo}', download.codigo)}
        </p>
      ) : null}
      {falhou ? (
        <p role="alert" className="mt-3 text-body-sm text-danger">
          {textoDaRecusa(falhou)}
        </p>
      ) : null}
    </>
  );
}
